import type { Rng } from '../rng.js';
import type { WaveGroupSpec, WaveSpec } from '../scenario.js';

/**
 * Wave generation (TRD-13 §5).
 *
 * Pacing, counts and composition are all generated. What keeps that from producing
 * nonsense is not restraint in the roller but the constraint set below — spikes cannot
 * cluster, archetypes cannot arrive before their gate, and no wave is entirely novel.
 */

export interface WaveGenData {
  threatCosts: Readonly<Record<string, number>>;
  archetypeGates: Readonly<Record<string, number>>;
  bossPool: readonly { enemy: string; minWave: number }[];
  /**
   * The boss every run ends on, whatever else the draw produced (playtest request).
   *
   * The uniform draw (B-20) is what stops the middle of a run being the same ladder
   * every time; the finale is the opposite problem. A run needs one fight it is always
   * building toward, and "which boss ends my game" is not a question the seed should
   * get to answer.
   */
  finalBoss?: string;
  tuning: {
    waveCount: readonly [number, number];
    baseThreat: number;
    threatGrowth: number;
    /**
     * Extra per-wave growth once a run is past `lateGrowthFromWave` (playtest request:
     * "a little more steep scaling on enemies longer in the runs").
     *
     * Separate from `threatGrowth` on purpose. Raising the base rate compounds from wave
     * one, so a change big enough to be felt at wave 20 has already made wave 5 a
     * different game. This one starts at zero effect and only bites where the complaint
     * was.
     */
    lateThreatGrowth: number;
    lateGrowthFromWave: number;
    spikeMultiplier: number;
    reliefMultiplier: number;
  };
}

/** Max distinct archetypes in one wave — more than three is a soup no plan survives. */
const MAX_ARCHETYPES = 3;

export function generateWaves(rng: Rng, data: WaveGenData, budgetScale = 1): WaveSpec[] {
  const count = rng.nextInt(data.tuning.waveCount[0], data.tuning.waveCount[1] + 1);
  const pacing = rollPacing(rng, count);

  const waves: WaveSpec[] = [];
  const seen = new Set<string>();
  const bossesUsed = new Set<string>();

  // Accumulated by repeated multiplication rather than `Math.pow`, whose precision is
  // engine-dependent (TRD-00 R-00-04). A client and a server disagreeing in the last bits
  // of a threat budget would generate different waves from the same seed.
  let growth = 1;

  for (let wave = 1; wave <= count; wave++) {
    if (wave > 1) growth *= data.tuning.threatGrowth;
    // The late-run ramp, compounded the same way and for the same reason: repeated
    // multiplication rather than `Math.pow`, whose last bits are engine-dependent.
    if (wave >= data.tuning.lateGrowthFromWave) growth *= data.tuning.lateThreatGrowth;
    const isBoss = wave % 5 === 0 || wave === count;

    if (isBoss) {
      // The last wave is always the finale — never a draw.
      const boss = wave === count && data.finalBoss
        ? data.finalBoss
        : pickBoss(rng, data, wave, bossesUsed);
      // A boss drawn past its gate scales up, so the wave-8 Comet rolled on wave 23
      // arrives as a wave-23 problem. Deterministic arithmetic, folded into the group's
      // hpMultiplier the director already honours.
      //
      // Was +10% per wave, uncapped, which compounded with the pool's own late-game HP
      // into the single biggest wall in the game: the reference player ended its run
      // ON a boss wave roughly half the time it died. +5% capped at +50% keeps the
      // "drawn late means drawn harder" intent without making the draw itself decide
      // the run.
      const minWave = data.bossPool.find((b) => b.enemy === boss)?.minWave ?? wave;
      const hpScale = 1 + Math.min(0.5, 0.05 * Math.max(0, wave - minWave));
      const groups: WaveGroupSpec[] = [
        { enemy: boss, count: 1, spacing: 1, delay: 1, hpMultiplier: hpScale },
      ];
      // Escort, drawn from what the player has already met.
      const escort = affordableArchetypes(data, wave);
      if (escort.length > 0 && wave > 5) {
        const chosen = rng.pick(escort);
        groups.push({
          enemy: chosen,
          count: rng.nextInt(6, 14),
          spacing: 0.5,
          delay: 4,
          hpMultiplier: 1,
        });
      }
      waves.push({ wave, groups, isBoss: true });
      continue;
    }

    const budget = data.tuning.baseThreat * growth * pacing[wave - 1]! * budgetScale;
    waves.push({ wave, groups: composeWave(rng, data, wave, budget, seen), isBoss: false });
  }

  return waves;
}

/**
 * Spike / relief placement (R-13-22).
 *
 * The constraints here are what make "fully generated pacing" survivable: without them a
 * roll can stack two spikes and a boss into three consecutive waves and end a run on
 * variance rather than on play.
 */
function rollPacing(rng: Rng, count: number): number[] {
  const pacing = new Array<number>(count).fill(1);
  const isBoss = (w: number): boolean => w % 5 === 0 || w === count;

  let spikes = 0;
  let reliefs = 0;
  for (let wave = 4; wave <= count && spikes < 4; wave++) {
    if (isBoss(wave) || isBoss(wave - 1)) continue; // never on, or straight after, a boss
    if (pacing[wave - 2] !== 1) continue; // never adjacent to another spike
    if (rng.chance(0.18)) {
      pacing[wave - 1] = 1.3;
      spikes++;
    }
  }
  for (let wave = 2; wave <= count && reliefs < 4; wave++) {
    if (isBoss(wave) || pacing[wave - 1] !== 1) continue;
    if (rng.chance(0.14)) {
      pacing[wave - 1] = 0.85;
      reliefs++;
    }
  }
  return pacing;
}

function affordableArchetypes(data: WaveGenData, wave: number): string[] {
  const bossIds = new Set(data.bossPool.map((b) => b.enemy));
  return (
    Object.keys(data.threatCosts)
      .filter((id) => !bossIds.has(id))
      .filter((id) => (data.archetypeGates[id] ?? 99) <= wave)
      // Splitterlings arrive by splitting, never by spawning.
      .filter((id) => id !== 'splitterling')
  );
}

/**
 * Spend the wave's threat budget on archetypes.
 *
 * At most three distinct kinds, and at least one the player has met before (R-13-24) —
 * a wave of entirely new threats gives them nothing to plan against.
 */
function composeWave(
  rng: Rng,
  data: WaveGenData,
  wave: number,
  budget: number,
  seen: Set<string>,
): WaveGroupSpec[] {
  const available = affordableArchetypes(data, wave);
  if (available.length === 0) {
    return [{ enemy: 'runner', count: 6, spacing: 0.7, delay: 0, hpMultiplier: 1 }];
  }

  const familiar = available.filter((id) => seen.has(id));
  const chosen: string[] = [];

  // Lead with something familiar whenever anything is.
  if (familiar.length > 0) chosen.push(rng.pick(familiar));

  while (chosen.length < MAX_ARCHETYPES) {
    const remaining = available.filter((id) => !chosen.includes(id));
    if (remaining.length === 0) break;
    if (chosen.length >= 1 && !rng.chance(0.55)) break;
    chosen.push(rng.pick(remaining));
  }
  if (chosen.length === 0) chosen.push(rng.pick(available));

  // Split the budget across the chosen kinds, weighted toward the first.
  const groups: WaveGroupSpec[] = [];
  let remainingBudget = budget;

  for (let i = 0; i < chosen.length; i++) {
    const id = chosen[i] as string;
    const cost = data.threatCosts[id] ?? 1;
    const share =
      i === chosen.length - 1 ? remainingBudget : remainingBudget * (0.5 + rng.nextFloat() * 0.2);
    remainingBudget -= share;

    const count = Math.max(1, Math.round(share / cost));
    seen.add(id);

    groups.push({
      enemy: id,
      count,
      spacing: +(0.3 + rng.nextFloat() * 0.9).toFixed(2),
      delay: i === 0 ? 0 : +(2 + rng.nextFloat() * 5).toFixed(2),
      hpMultiplier: 1,
    });
  }

  return rescaleDuration(groups);
}

/**
 * Keep a wave between 20 and 45 seconds (R-13-26).
 *
 * Spacings are rescaled rather than the wave regenerated — the composition is already
 * valid, and rerolling it over a timing detail would throw away a good wave.
 */
function rescaleDuration(groups: WaveGroupSpec[]): WaveGroupSpec[] {
  const duration = groups.reduce((max, g) => Math.max(max, g.delay + g.count * g.spacing), 0);
  if (duration >= 20 && duration <= 45) return groups;

  const scale = duration <= 0 ? 1 : (duration < 20 ? 20 : 45) / duration;
  return groups.map((g) => ({
    ...g,
    spacing: +Math.max(0.15, g.spacing * scale).toFixed(2),
    delay: +(g.delay * scale).toFixed(2),
  }));
}

/**
 * Seeded-uniform draw with a no-repeat-within-run rule (backlog B-20).
 *
 * The old strongest-biased pick made every run the same ladder — wave 5 was always
 * Bulwark, wave 10 nearly always Hive. Uniform over the eligible pool gives each seed
 * its own boss order; excluding bosses already fought this run (until the pool is
 * exhausted) keeps a 25-wave daily from rolling the same fight twice. Late waves rolling
 * an early boss are handled by the caller's HP scale, not by biasing the draw.
 */
function pickBoss(rng: Rng, data: WaveGenData, wave: number, used: Set<string>): string {
  // The finale is reserved. Without this a long run can draw Prism at wave 25 and then
  // be handed it again at 26, which is both a repeat and a spoiled ending.
  const eligible = data.bossPool.filter(
    (b) => b.minWave <= wave && b.enemy !== data.finalBoss,
  );
  if (eligible.length === 0) return data.bossPool[0]?.enemy ?? 'boss-bulwark';
  const fresh = eligible.filter((b) => !used.has(b.enemy));
  const picked = rng.pick(fresh.length > 0 ? fresh : eligible).enemy;
  used.add(picked);
  return picked;
}
